import React, { useCallback, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import FamilyTreeCitationEditor from './FamilyTreeCitationEditor';
import {
  createFamilyTreeCitation,
  deleteFamilyTreeCitation,
  loadFamilyTreeCitations,
  updateFamilyTreeCitation,
} from './familyTreeCitationApi';

const { FiBookOpen, FiPlus, FiExternalLink, FiEdit2, FiTrash2 } = FiIcons;

function safeCitationUrl(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export default function FamilyTreeCitationManager({ memorialId, node }) {
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await loadFamilyTreeCitations(memorialId, node?.id);
    if (result?.status === 'success') {
      setCitations(Array.isArray(result?.data) ? result.data : []);
      setError('');
    } else {
      setCitations([]);
      setError(result?.error ?? 'Could not load citations.');
    }
    setLoading(false);
  }, [memorialId, node?.id]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const result = await loadFamilyTreeCitations(memorialId, node?.id);
      if (!active) return;
      if (result?.status === 'success') {
        setCitations(Array.isArray(result?.data) ? result.data : []);
        setError('');
      } else {
        setCitations([]);
        setError(result?.error ?? 'Could not load citations.');
      }
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [memorialId, node?.id]);

  const saveCitation = async (fields) => {
    setSaving(true);
    setError('');
    const now = new Date().toISOString();
    const payload = {
      ...fields,
      memorial_id: String(memorialId ?? ''),
      family_member_id: String(node?.id ?? ''),
      family_member_name: node?.name ?? '',
      created_at: editing?.created_at ?? now,
      updated_at: now,
    };
    const result = editing?.id !== undefined && editing?.id !== null
      ? await updateFamilyTreeCitation(editing.id, payload)
      : await createFamilyTreeCitation(payload);

    if (result?.status === 'success') {
      setEditorOpen(false);
      setEditing(null);
      await refresh();
    } else {
      setError(result?.error ?? 'Could not save this citation.');
    }
    setSaving(false);
  };

  const removeCitation = async (citation) => {
    if (citation?.id === undefined || citation?.id === null) return;
    if (!window.confirm(`Remove the citation “${citation?.source_title ?? 'this source'}”?`)) return;
    const result = await deleteFamilyTreeCitation(citation.id);
    if (result?.status === 'success') {
      await refresh();
    } else {
      setError(result?.error ?? 'Could not remove this citation.');
    }
  };

  return (
    <section className="rounded-xl border border-brand-dark bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <SafeIcon icon={FiBookOpen} className="h-3.5 w-3.5 shrink-0 text-accent-gold" />
          <span className="truncate text-[9px] font-black uppercase tracking-widest text-accent-navy">
            Citations ({citations.length})
          </span>
        </div>
        <button
          type="button"
          onClick={() => { setEditing(null); setEditorOpen(value => !value); }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-brand-dark px-2 py-1 text-[8px] font-black uppercase tracking-widest text-text-muted transition-colors hover:border-accent-gold hover:text-accent-navy"
        >
          <SafeIcon icon={FiPlus} className="h-3 w-3" />
          Add
        </button>
      </div>

      {editorOpen && (
        <FamilyTreeCitationEditor
          key={editing?.id ?? 'new-citation'}
          citation={editing}
          saving={saving}
          onCancel={() => { setEditorOpen(false); setEditing(null); }}
          onSave={saveCitation}
        />
      )}

      {error && <p role="alert" className="mt-2 text-[10px] leading-relaxed text-red-600">{error}</p>}
      {loading && <p className="py-2 text-[10px] text-text-muted">Loading citations…</p>}
      {!loading && citations.length === 0 && !error && (
        <p className="py-2 text-[10px] leading-relaxed text-text-muted">
          No sources yet. Add a citation to document where this family record came from.
        </p>
      )}

      <div className="mt-2 space-y-2">
        {citations.map((citation, index) => {
          const href = safeCitationUrl(citation?.source_url ?? '');
          return (
            <article key={citation?.id ?? `${citation?.source_title ?? 'source'}-${index}`} className="rounded-lg border border-brand-light p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="break-words text-[11px] font-bold text-accent-navy">
                    {citation?.source_title ?? ''}
                  </p>
                  {citation?.source_type && (
                    <p className="mt-0.5 text-[8px] font-black uppercase tracking-widest text-text-muted">
                      {citation.source_type}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {href && (
                    <a href={href} target="_blank" rel="noreferrer" aria-label="Open source"
                      className="rounded p-1 text-accent-navy transition-colors hover:text-accent-gold">
                      <SafeIcon icon={FiExternalLink} className="h-3 w-3" />
                    </a>
                  )}
                  {citation?.id !== undefined && citation?.id !== null && (
                    <>
                      <button type="button" aria-label="Edit citation"
                        onClick={() => { setEditing(citation); setEditorOpen(true); }}
                        className="rounded p-1 text-accent-navy transition-colors hover:text-accent-gold">
                        <SafeIcon icon={FiEdit2} className="h-3 w-3" />
                      </button>
                      <button type="button" aria-label="Delete citation"
                        onClick={() => removeCitation(citation)}
                        className="rounded p-1 text-text-muted transition-colors hover:text-red-600">
                        <SafeIcon icon={FiTrash2} className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {citation?.citation_note && (
                <p className="mt-2 whitespace-pre-wrap break-words text-[10px] leading-relaxed text-text-muted">
                  {citation.citation_note}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}